import { useCallback, useState } from 'react'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

type UseFirstTimeLikeCoachmark = {
  /** Whether the coachmark should currently be shown. */
  open: boolean
  /**
   * Call when the user likes for the first time. Opens the coachmark only if
   * it hasn't been dismissed before for this network.
   */
  trigger: () => void
  /**
   * Hide the coachmark without remembering it, so a later first like still
   * gets the explanation. Use when the like it explained did not go through.
   */
  close: () => void
  /** Dismiss the coachmark and remember it, so it won't show again. */
  dismiss: () => void
}

/**
 * One-time "likes go one way" coachmark shown the first time an actor likes a
 * comment on a non-interoperable network. Dismissal is remembered per network
 * (so the explanation appears once per channel type the actor uses).
 */
export const useFirstTimeLikeCoachmark = (
  service: string,
): UseFirstTimeLikeCoachmark => {
  const { isActive, dismiss: dismissBanner } = useDismissableBanner(
    `comments-first-time-like-oneway-${service}`,
    { trackDismiss: true },
  )
  const [open, setOpen] = useState(false)

  const trigger = useCallback(() => {
    if (isActive) {
      setOpen(true)
    }
  }, [isActive])

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const dismiss = useCallback(() => {
    setOpen(false)
    dismissBanner()
  }, [dismissBanner])

  return { open, trigger, close, dismiss }
}
